// Role-editor load/serialize — the PURE transforms extracted out of the 2400-line
// editRole.js component (Step 2 of the anchor de-special-casing). Keeping these pure
// is what makes the '*' (grant-all / anchor) round-trip TESTABLE: a role doc loaded
// into editor state and serialized back must be byte-identical (RT-1..RT-11 +
// wildcardParity.test.js). The dangerous direction is serialize — a regression here
// silently destroys a role's grants — so it lives here, under test, not inline.
//
// '*' becomes an ordinary editor value carried in a small separate `anchorGrant`
// state ({ base, solution }: the rank of the '*' tab on the '*' module row and the
// '*' service row). The '*' rows never reach the catalogue-driven matrices (the
// module '*' is off-catalogue; the service '*' is not a real serviceId), so they are
// loaded/serialized here explicitly rather than as ordinary rows.

import { rank as permRank } from "./permissionDependencies";
import { mergeSwitches } from "./sharingSwitches";

const WILD = "*";
const permOf = (r) => (r >= 2 ? "ALL" : r >= 1 ? "READ" : "disable");
const clampRank = (r) => (r >= 2 ? 2 : r >= 1 ? 1 : 0);

// The assets offered per solution row (mirror of editRole.js's module-level `assets`
// const). Passed in by the caller so the two stay in lockstep; this default is only a
// fallback for tests that don't supply one.
export const DEFAULT_ASSETS = [
  "Monitoring", "Controlling", "Configuration", "Alarms", "Events", "Analytics",
  "Group Management", "Tracking", "Rules", "Video Analytics", "Digital Twin",
  "History", "Metadata", "Threshold Profiles", "Scheduled Reports",
];

// The asset allowlist that decides whether a given asset column is OFFERED for a
// service — mirror of editRole.js:561-581. `svcMeta` is the metaData for the service
// (from getServices), `allServicesEntry` is the metaData.services entry (redux) for
// the same service by name (undefined-safe).
function assetOffered(asset, svcMeta, allServicesEntry) {
  if (allServicesEntry && (allServicesEntry.tabs || []).some((t) => t && t.name === asset))
    return true;
  switch (asset) {
    case "Monitoring":
    case "Controlling":
    case "Alarms":
    case "Events":
    case "Analytics":
    case "History":
    case "Metadata":
    case "Group Management":
    case "Configuration":
    case "Threshold Profiles":
    case "Scheduled Reports":
      return true;
    default:
      break;
  }
  const tabs = (svcMeta && svcMeta.tabs) || {};
  if (asset === "Tracking") return !!tabs.location;
  if (asset === "Rules") return !!tabs.maintenance;
  if (asset === "Digital Twin") return !!(tabs.digitalTwin && tabs.digitalTwin.value);
  if (asset === "Video Analytics") return !!tabs.videoAnalytics;
  return false;
}

// Derive { base, solution } for the '*' rows of a role doc. Prefers the authoritative
// BE-supplied `wildcardGrant` (serviceRoleAggregation), with a rows fallback so a
// stale BE (old image) still yields the right value. 0 = the row is absent/gutted.
export function deriveAnchorGrant(roleDoc) {
  if (!roleDoc) return { base: 0, solution: 0 };
  const wg = roleDoc.wildcardGrant;
  if (wg && (typeof wg.base === "number" || typeof wg.solution === "number")) {
    return { base: clampRank(wg.base || 0), solution: clampRank(wg.solution || 0) };
  }
  const wildRowRank = (rows, idKey, idVal) => {
    const row = (rows || []).find(
      (x) => x && String(x[idKey]) === idVal
    );
    if (!row) return 0;
    return Math.max(
      0,
      ...(row.tabs || []).map((t) => (t && t.name === WILD ? permRank(t.permission) : 0))
    );
  };
  return {
    base: clampRank(wildRowRank(roleDoc.modules, "name", WILD)),
    solution: clampRank(wildRowRank(roleDoc.services, "serviceId", WILD)),
  };
}

// ── LOAD ─────────────────────────────────────────────────────────────────────
// Build the editor's `utils` (Base Permissions matrix) from a role doc + catalogue.
// Faithful mirror of editRole.js:416-479. Pure: deep-clones both inputs.
export function loadModulesIntoUtils(roleDoc, allUtils) {
  let modules = JSON.parse(JSON.stringify((roleDoc && roleDoc.modules) || []));
  const catalogue = JSON.parse(JSON.stringify(allUtils || []));
  // Drop off-catalogue modules (deprecated) and off-catalogue tabs — the intentional
  // deprecation/cleanup path. NEVER applied to the '*' module (handled via anchorGrant).
  modules = modules.filter(
    (m) => m && m.name !== WILD && catalogue.some((u) => u.name === m.name)
  );
  modules = modules.map((m) => {
    const cat = catalogue.find((u) => u.name === m.name);
    if (!cat) return m;
    const catNames = new Set(cat.tabs.filter((t) => t.name).map((t) => t.name));
    return { ...m, tabs: (m.tabs || []).filter((t) => !t || !t.name || catNames.has(t.name)) };
  });
  // A row left with no catalogue tab (a Solution Management row holding only the sharing
  // switches, which the editor keeps in their own state) counts as missing: it is laid out where
  // the catalogue puts missing rows, as before the switches existed, and shows Disabled.
  modules = modules.filter((m) => (m.tabs || []).some((t) => t && t.name));
  catalogue.forEach((mod) => {
    const ind = modules.findIndex((m) => m.name === mod.name);
    if (ind !== -1) {
      mod.tabs.forEach((t) => {
        if (!t.name) {
          modules.find((m) => m.name === mod.name)?.tabs.push({ name: "", permissions: "" });
        } else if (!modules.find((m) => m.name === mod.name)?.tabs.find((tab) => tab.name === t.name)) {
          modules.find((m) => m.name === mod.name)?.tabs.push({ name: t.name, permission: "disable" });
        }
      });
      const named = modules.find((s) => s.name === mod.name)?.tabs.filter((t) => t.name) || [];
      const allCnt = modules.find((s) => s.name === mod.name)?.tabs.filter((t) => t.permission === "ALL").length;
      const readCnt = modules.find((s) => s.name === mod.name)?.tabs.filter((t) => t.permission === "READ").length;
      modules[ind].overall = allCnt === named.length ? "ALL" : readCnt === named.length ? "READ" : "";
    } else {
      modules.push({
        name: mod.name,
        tabs: mod.tabs.map((m) => ({ name: m.name, permission: m.permission ? "disable" : "" })),
        overall: "disable",
      });
    }
  });
  // O24 (owner 2026-10-21): every role shows its rows in catalogue order (Administration, Solution
  // Features, Analytics, Solution Management, Solution Edit), whatever order it was stored in. Every row
  // here is a catalogue row (off-catalogue rows were dropped above); ties keep their order.
  const pos = (m) => catalogue.findIndex((u) => u.name === m.name);
  return modules
    .map((m, i) => [m, i])
    .sort((a, b) => pos(a[0]) - pos(b[0]) || a[1] - b[1])
    .map((x) => x[0]);
}

// Build the editor's `servicePermissions` (Per Solution matrix) from a role doc +
// the tenant service list. Faithful mirror of editRole.js:550-633 for the EDIT case
// (existing role -> no caller clamp; that stays in the component's create path).
export function loadServicesIntoPermissions(roleDoc, servicesList, allServicesMeta, assets, opts) {
  const assetList = assets || DEFAULT_ASSETS;
  // createMode: no role doc yet — every offered asset defaults to "ALL" (the operational
  // create seed; the caller-clamp in the component then caps it for a delegated admin).
  const createMode = !!(opts && opts.createMode);
  const roleServices = (roleDoc && roleDoc.services) || [];
  return (servicesList || []).map((service) => {
    let meta = service.metaData;
    if (typeof meta === "string") { try { meta = JSON.parse(meta); } catch (e) { meta = {}; } }
    meta = meta || { tabs: {} };
    const sid = service._id;
    const roleService = roleServices.find((s) => String(s.serviceId) === String(sid));
    const grp = roleService && roleService.group
      ? { name: roleService.group.name || "", id: roleService.group.id || "" }
      : { name: "", id: "" };
    const allServicesEntry = (allServicesMeta || []).find((n) => n.name === service.name);
    const permOfAsset = (assetName) =>
      createMode ? "ALL" : (roleService?.tabs?.find((t) => t.name === assetName)?.permission || "disable");
    const assetsArr = assetList.reduce((acc, asset) => {
      if (assetOffered(asset, meta, allServicesEntry)) {
        acc.push({ name: asset, group: { ...grp }, permissions: permOfAsset(asset) });
      }
      return acc;
    }, []);
    // overall = operational summary over ALL stored tabs of the role's row (mirror of
    // editRole.js:611-631: compares against tabs.length of the role's stored row).
    let overall;
    if (createMode) overall = "ALL";
    else {
      const storedTabs = roleService?.tabs || [];
      if (!roleService) overall = "disable";
      else {
        const allCnt = storedTabs.filter((t) => t.permission === "ALL").length;
        const readCnt = storedTabs.filter((t) => t.permission === "READ").length;
        overall = allCnt === storedTabs.length ? "ALL" : readCnt === storedTabs.length ? "READ" : "";
      }
    }
    return { name: service.name, id: sid, group: { ...grp }, assets: assetsArr, serviceId: sid, overall };
  });
}

export function loadRoleIntoEditorState(roleDoc, allUtils, servicesList, allServicesMeta, assets) {
  return {
    utils: loadModulesIntoUtils(roleDoc, allUtils),
    servicePermissions: loadServicesIntoPermissions(roleDoc, servicesList, allServicesMeta, assets),
    anchorGrant: deriveAnchorGrant(roleDoc),
  };
}

// ── SERIALIZE ──────────────────────────────────────────────────────────────────
// Build the PUT/POST body from editor state. Faithful mirror of editRole.js:770-826,
// PLUS the '*' emit path. The '*' rows are emitted FIRST (unshift), before enumerated
// rows, so the BE and a diff see them at the head.
export function serializeEditorState({
  name,
  description,
  utils,
  servicePermissions,
  anchorGrant,
  loadedAnchorGrant,
  groupIds,
  allowDropWildcard,
  switches,
}) {
  const body = { name, description, services: [], modules: [] };
  const groups = groupIds || [];

  // Per-solution rows: include a row when ANY grant exists (operational + scoping).
  (servicePermissions || []).forEach((service) => {
    const hasAnyGrant = (service.assets || []).some(
      (a) => a && a.permissions && a.permissions !== "disable"
    );
    if (!hasAnyGrant) return;
    const tempGroup = groups.find((g) => g.serviceId === service.serviceId);
    const row = {
      serviceId: service.serviceId,
      tabs: [],
      group: {
        id: tempGroup?.id && tempGroup?.id !== "0" ? tempGroup?.id : "",
        name: tempGroup?.name,
      },
    };
    service.assets.forEach((asset) => {
      if (asset.permissions !== "disable") {
        row.tabs.push({ name: asset.name, permission: asset.permissions });
      }
    });
    body.services.push(row);
  });

  // Base Permissions rows: a module contributes only when it has >=1 granted tab.
  const modules = [];
  (utils || []).forEach((util) => {
    if (util.overall === "disable") return;
    const row = { name: util.name, tabs: [] };
    (util.tabs || [])
      .filter((t) => t.name)
      .forEach((tab) => {
        if (tab.permission !== "disable") row.tabs.push({ name: tab.name, permission: tab.permission });
      });
    modules.push(row);
  });
  modules.forEach((mod) => {
    if (mod.tabs.length) body.modules.push(mod);
  });
  // Solution sharing switches (kept apart from the matrix, utils/sharingSwitches.js): merged into
  // the Solution Management row, or a row of their own. Absent `switches` = none (create/old callers).
  body.modules = mergeSwitches(body.modules, switches);

  // '*' round-trip. desired = the editor's current anchorGrant; orig = loaded value.
  // ANY reduction of a held '*' rank (ALL->READ, ALL->0, READ->0) is honoured ONLY with
  // per-axis confirmation, because the BE's LD-3 wildcardRankLoss guard rejects EVERY
  // downward change of a '*' rank (not just removal-to-zero) unless body.dropWildcard is
  // set. Without confirmation, re-emit the ORIGINAL rank so a serializer path can never
  // reduce or destroy a grant-all. allowDropWildcard may be a boolean (both axes) or
  // { base, solution } (per-axis — the confirmation dialog scopes it to the axis touched).
  const ag = anchorGrant || { base: 0, solution: 0 };
  const orig = loadedAnchorGrant || { base: 0, solution: 0 };
  const allowFor = (axis) =>
    allowDropWildcard && typeof allowDropWildcard === "object"
      ? !!allowDropWildcard[axis]
      : !!allowDropWildcard;
  let dropWildcard = false;
  const effAxis = (desired, was, axis) => {
    if (desired < was && was >= 1) {
      // a reduction of a held '*'
      if (!allowFor(axis)) return was; // guard: no unconfirmed reduction/destroy
      dropWildcard = true;
      return desired;
    }
    return desired; // raise or unchanged
  };
  const effBase = effAxis(ag.base || 0, orig.base || 0, "base");
  const effSolution = effAxis(ag.solution || 0, orig.solution || 0, "solution");
  if (effBase >= 1) body.modules.unshift({ name: WILD, tabs: [{ name: WILD, permission: permOf(effBase) }] });
  if (effSolution >= 1) body.services.unshift({ serviceId: WILD, tabs: [{ name: WILD, permission: permOf(effSolution) }] });
  if (dropWildcard) body.dropWildcard = true;

  return body;
}

// Unsaved-changes key for the role editor: equal keys mean the role that would be saved
// is the same. Built on serializeEditorState, so editor-only decorations (row Overall,
// highlight state, group ids of "0", rows with no grant) never count as a change, and
// changing a cell and changing it back is no change. The '*' rows mirror anchorGrant
// exactly (loaded = current, drop allowed), and the dropWildcard flag is left out.
export function editorChangeKey({
  name,
  description,
  utils,
  servicePermissions,
  anchorGrant,
  groupIds,
  switches,
}) {
  const body = serializeEditorState({
    name,
    description,
    utils,
    servicePermissions,
    anchorGrant,
    loadedAnchorGrant: anchorGrant,
    groupIds,
    allowDropWildcard: true,
    switches,
  });
  return JSON.stringify([name, description, body.modules, body.services]);
}

// Name tidy-up for a created or cloned role: trims the ends and turns each run of spaces
// into one ("Admin  -   clone" -> "Admin - clone"). Single spaces and hyphens are kept as
// typed, so a suggested clone name ("X (Clone)") is saved unchanged and the next clone's
// taken-name check finds it. (It used to turn every space and hyphen into " - ".)
export function removeExtraSpaces(text) {
  return text.trim().replace(/\s+/g, " ");
}
